// 파일검사 패널 — /admin/scan 호출 (어드민 토큰 자동 사용)
import { useRef, useState } from 'react';
import { validateFile } from '../lib/policies';
import { fileIcon, formatSize } from '../lib/fileUtils';
import { useUI } from '../lib/uiStore';
import { usePolicyStore } from '../lib/policyStore';
import { useScanStore, type ScanStatus } from '../lib/scanStore';
import { renderSafeHtml } from '../lib/sanitize';

const ADMIN_TOKEN = (import.meta as unknown as { env: Record<string, string> }).env?.VITE_ADMIN_TOKEN ?? '';

type QueueStatus = 'scanning' | 'blocked' | 'safe' | 'warn' | 'danger';
interface QueueItem {
  id: string;
  fid: string;
  name: string;
  size: string;
  icon: string;
  iconCls: string;
  status: QueueStatus;
  progress: number;
  stage: string;
  policyId: string;
  reason?: string;
  detailHtml?: string;
}

interface WebhookEntry {
  seq: number;
  time: string;
  payload: Record<string, unknown>;
}

export function TestPage() {
  const policies = usePolicyStore((s) => s.policies);
  const [currentPolicy, setCurrentPolicy] = useState<string>(() => Object.keys(policies)[0] ?? 'default');
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [webhooks, setWebhooks] = useState<WebhookEntry[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { showToast } = useUI();
  const { addJob, updateFileId, updateJobStatus, addActivity } = useScanStore();
  const webhookSeqRef = useRef(0);

  const effectivePolicyId = policies[currentPolicy] ? currentPolicy : (Object.keys(policies)[0] ?? 'default');
  const policyOrNull = policies[effectivePolicyId] ?? null;
  if (!policyOrNull) {
    return <div className="card" style={{ padding: 24 }}>정책이 없습니다. 정책 관리에서 추가하세요.</div>;
  }
  const policy = policyOrNull;

  function sendWebhook(payload: Record<string, unknown>) {
    webhookSeqRef.current += 1;
    setWebhooks((prev) => [{ seq: webhookSeqRef.current, time: new Date().toLocaleTimeString(), payload }, ...prev].slice(0, 5));
  }

  function handleFiles(files: File[]) {
    if (!files.length) return;
    let arr = files;
    if (arr.length > policy.maxFiles) {
      showToast(`최대 ${policy.maxFiles}개까지 가능합니다`, 'error');
      arr = arr.slice(0, policy.maxFiles);
    }
    arr.forEach((f) => processFile(f));
  }

  function processFile(file: File) {
    const { icon, cls } = fileIcon(file.name);
    const id = `q_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    const fid = `f_${Math.random().toString(36).slice(2, 14)}`;
    const sizeStr = formatSize(file.size);
    const r = validateFile(file, policy);

    if (!r.ok) {
      const reason =
        r.reason === 'extension_blocked'
          ? `⛔ 정책 위반: "${r.ext}"는 차단 확장자입니다 (policy: ${effectivePolicyId})`
          : r.reason === 'size_exceeded'
            ? `⛔ 정책 위반: 최대 크기 ${policy.maxSizeMB}MB 초과 (현재: ${r.sizeMB.toFixed(1)}MB)`
            : `⛔ 정책 위반: "${r.ext}"는 허용되지 않은 확장자입니다`;

      setQueue((prev) => [
        ...prev,
        { id, fid, name: file.name, size: sizeStr, icon, iconCls: cls, status: 'blocked', progress: 0, stage: '', policyId: effectivePolicyId, reason },
      ]);
      addJob({ fileId: fid, filename: file.name, size: sizeStr, policy: effectivePolicyId, status: 'blocked', engines: '⊘ 정책 차단', source: 'real' });
      addActivity('red', `정책 위반 차단 <code>${file.name}</code> — ${r.reason}`);
      sendWebhook({ event: 'scan.blocked', file_id: fid, filename: file.name, status: 'blocked', reason: r.reason });
      return;
    }

    setQueue((prev) => [
      ...prev,
      { id, fid, name: file.name, size: sizeStr, icon, iconCls: cls, status: 'scanning', progress: 5, stage: `QUEUED · ${effectivePolicyId}`, policyId: effectivePolicyId },
    ]);
    addJob({ fileId: fid, filename: file.name, size: sizeStr, policy: effectivePolicyId, status: 'pending', engines: '대기 중', source: 'real' });
    sendWebhook({ event: 'scan.pending', file_id: fid, filename: file.name, status: 'pending' });

    void submitToApi(file, fid, id, sizeStr);
  }

  async function submitToApi(file: File, fid: string, id: string, sizeStr: string) {
    void sizeStr;
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('policy_id', effectivePolicyId);

      const res = await fetch('/admin/scan', {
        method: 'POST',
        headers: { 'X-Admin-Token': ADMIN_TOKEN },
        body: form,
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(`HTTP ${res.status}: ${text}`);
      }

      const data = await res.json() as { file_id?: string; status?: string };
      const realFileId = data.file_id ?? fid;

      // 서버가 발급한 진짜 file_id로 scanStore ID 동기화 (로그 중복 방지)
      if (realFileId !== fid) updateFileId(fid, realFileId);

      if (data.status === 'blocked') {
        finalizeResult(id, realFileId, file.name, 'blocked', '', undefined);
        return;
      }

      updateJobStatus(realFileId, 'scanning', 'API 검사 중...');
      setQueue((prev) => prev.map((q) => q.id === id ? { ...q, fid: realFileId, stage: 'API 검사 중...' } : q));
      pollResult(realFileId, id, file.name);
    } catch (err) {
      const msg = (err as Error).message;
      showToast(`오류: ${msg}`, 'error');
      setQueue((prev) => prev.map((q) => q.id === id ? { ...q, status: 'danger', stage: '✗ 오류', progress: 100 } : q));
      updateJobStatus(fid, 'failed', '오류');
    }
  }

  async function pollResult(fileId: string, id: string, filename: string) {
    const MAX_POLLS = 30;
    let attempts = 0;
    const interval = setInterval(async () => {
      attempts++;
      try {
        const res = await fetch(`/admin/scan/${fileId}`, { headers: { 'X-Admin-Token': ADMIN_TOKEN } });
        if (!res.ok) throw new Error(`${res.status}`);
        const data = await res.json() as { status: string; engines?: Record<string, unknown>; threat_label?: string };

        if (data.status === 'pending' || data.status === 'scanning') {
          if (attempts >= MAX_POLLS) {
            clearInterval(interval);
            finalizeResult(id, fileId, filename, 'failed', '타임아웃', undefined);
          }
          return;
        }

        clearInterval(interval);
        const enginesStr = data.engines
          ? Object.entries(data.engines).map(([k, v]) => {
              const verdict = (v as Record<string, unknown>).verdict ?? v;
              return `${verdict === 'clean' ? '✓' : '✗'} ${k}`;
            }).join(' · ')
          : '';
        finalizeResult(id, fileId, filename, data.status, enginesStr, data.threat_label);
      } catch {
        clearInterval(interval);
        finalizeResult(id, fileId, filename, 'failed', '오류', undefined);
      }
    }, 1500);
  }

  function finalizeResult(id: string, fileId: string, filename: string, status: string, enginesStr: string, threat?: string) {
    const qStatus: QueueStatus =
      status === 'safe' ? 'safe'
        : status === 'quarantined' || status === 'quarantine' ? 'warn'
          : status === 'blocked' ? 'blocked'
            : 'danger';

    const stage =
      qStatus === 'safe' ? '✓ SAFE'
        : qStatus === 'warn' ? '⚠ QUARANTINED'
          : qStatus === 'blocked' ? '⛔ BLOCKED'
            : '✗ FAILED';

    const detailHtml =
      qStatus === 'warn' ? `<strong>격리 처리</strong> · ${threat ?? '의심 패턴 탐지'}`
        : qStatus === 'danger' ? `<strong>위협 감지</strong> · ${threat ?? '악성코드 탐지'}`
          : undefined;

    setQueue((prev) =>
      prev.map((q) => q.id === id ? { ...q, status: qStatus, stage, progress: 100, detailHtml } : q),
    );

    const scanStatus: ScanStatus =
      status === 'safe' ? 'safe'
        : status === 'quarantined' || status === 'quarantine' ? 'quarantine'
          : status === 'blocked' ? 'blocked'
            : 'failed';

    updateJobStatus(fileId, scanStatus, enginesStr || status);

    const actType = qStatus === 'safe' ? 'green' : qStatus === 'warn' ? 'yellow' : 'red';
    const actMsg =
      qStatus === 'safe'
        ? `<code>${filename}</code> 검사 완료 — <strong>안전</strong> (${enginesStr})`
        : `<code>${filename}</code> ${qStatus === 'warn' ? '격리 처리' : '위협 감지'} — ${threat ?? enginesStr}`;
    addActivity(actType, actMsg);
    sendWebhook({ event: 'scan.completed', file_id: fileId, filename, status });

    if (qStatus === 'safe') showToast(`${filename} — 검사 통과`, 'success');
    else if (qStatus === 'warn') showToast(`${filename} — 격리 처리됨`, 'warn');
    else showToast(`${filename} — 위협 감지!`, 'error');
  }

  return (
    <>
      <div className="sect-head">
        <div className="sect-title">⬆ 파일검사 <em>실제 API 호출</em></div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 10, fontWeight: 500 }}>1. 적용할 정책 선택</div>
        <div className="policy-grid">
          {Object.entries(policies).map(([id, p]) => {
            const isSel = id === effectivePolicyId;
            return (
              <button
                type="button"
                key={id}
                data-policy={id}
                className={`policy-card ${isSel ? 'selected' : ''}`}
                onClick={() => setCurrentPolicy(id)}
                style={{ textAlign: 'left', fontFamily: 'inherit' }}
              >
                <div className="policy-name">{p.name} <span className="radio-mark" /></div>
                <div className="policy-id">policy_id: {id}</div>
                <div className="policy-rules">
                  <div className="policy-rule"><span>최대 크기</span><strong>{p.maxSizeMB >= 1024 ? `${(p.maxSizeMB / 1024).toFixed(0)} GB` : `${p.maxSizeMB} MB`}</strong></div>
                  <div className="policy-rule"><span>다중 업로드</span><strong>{p.maxFiles}개</strong></div>
                  <div className="policy-rule"><span>허용 확장자</span><strong>{p.allowedExt.length}종</strong></div>
                  <div className="policy-rule"><span>엔진</span><strong>{p.engines.join(' + ')}</strong></div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 10, fontWeight: 500 }}>2. 파일 업로드</div>
      <div
        className={`upload-zone ${dragOver ? 'dragover' : ''}`}
        id="dropZone"
        onClick={() => fileInputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFiles(Array.from(e.dataTransfer.files)); }}
      >
        <div className="upload-icon">📁</div>
        <div className="upload-text">파일을 드래그하거나 클릭하여 업로드</div>
        <div className="upload-sub">선택한 정책에 따라 검사가 진행됩니다 · 의심 파일은 자동 격리됩니다</div>
        <input
          type="file"
          id="fileInput"
          ref={fileInputRef}
          multiple
          aria-label="파일 입력"
          onChange={(e) => { handleFiles(Array.from(e.target.files ?? [])); e.target.value = ''; }}
        />
      </div>

      <div className="active-policy" id="activePolicyDisplay">
        <span className="active-policy-label">ACTIVE POLICY →</span>
        <span className="policy-chip">{effectivePolicyId}</span>
        <span className="policy-chip">max: {policy.maxSizeMB}MB</span>
        <span className="policy-chip">files: {policy.maxFiles}</span>
        <span className="policy-chip">ext: {policy.allowedExt.length}종</span>
        <span className="policy-chip">engines: {policy.engines.join(',')}</span>
      </div>

      <div className="sect-head mt-20">
        <div className="sect-title">📤 검사 대기열 <em>{queue.length > 0 ? `(${queue.length})` : ''}</em></div>
        {queue.length > 0 && (
          <button className="btn btn-ghost btn-sm" onClick={() => setQueue([])}>초기화</button>
        )}
      </div>
      <div className="queue" id="queueList">
        {queue.map((q) => <QueueRow key={q.id} q={q} />)}
      </div>

      <div className="card mt-20">
        <div className="card-head">
          <div className="card-head-title">📡 Webhook Callback (실시간)</div>
          <span className="text-mono text-muted">POST callback_url</span>
        </div>
        <div className="webhook-sim" style={{ margin: 0, border: 'none', borderRadius: 0 }}>
          <div className="webhook-head"><span className="ico" />WAITING FOR FILE UPLOAD...</div>
          <div className="webhook-body" id="webhookBody">
            {webhooks.length === 0
              ? <span>{'// 파일을 업로드하면 webhook payload가 여기에 표시됩니다'}</span>
              : webhooks.map((w) => <WebhookRow key={w.seq} w={w} />)}
          </div>
        </div>
      </div>
    </>
  );
}

function QueueRow({ q }: { q: QueueItem }) {
  const showProgress = q.status !== 'blocked';
  const pfCls = q.status === 'scanning' ? 'pf-scan' : q.status === 'safe' ? 'pf-ok' : q.status === 'warn' ? 'pf-warn' : 'pf-danger';
  const ssCls = q.status === 'scanning' ? 'ss-scan' : q.status === 'safe' ? 'ss-ok' : q.status === 'warn' ? 'ss-warn' : 'ss-danger';
  return (
    <div className="queue-item" id={q.id}>
      <div className={`file-icon ${q.iconCls}`}>{q.icon}</div>
      <div className="file-info">
        <div className="file-name">{q.name}</div>
        <div className="file-meta">{q.size} · {q.policyId}</div>
        {q.reason && <div className="reason-box reason-danger">{q.reason}</div>}
        {q.detailHtml && (
          <div className={`reason-box ${q.status === 'warn' ? 'reason-warn' : 'reason-danger'}`}>
            {q.status === 'warn' ? '⚠' : '⛔'} <div>{renderSafeHtml(q.detailHtml)}</div>
          </div>
        )}
      </div>
      {showProgress ? (
        <div className="progress-wrap">
          <div className="progress"><div className={`progress-fill ${pfCls}`} style={{ width: `${q.progress}%` }} /></div>
          <div className={`scan-status ${ssCls}`}>{q.stage}</div>
        </div>
      ) : (
        <div className="scan-status ss-danger" style={{ minWidth: 80, textAlign: 'right' }}>⛔ BLOCKED</div>
      )}
    </div>
  );
}

function WebhookRow({ w }: { w: WebhookEntry }) {
  const status = String(w.payload.status ?? '');
  const color = status === 'safe' ? 'var(--accent)' : status === 'pending' ? 'var(--blue)' : 'var(--red)';
  return (
    <div style={{ paddingBottom: 12, borderBottom: '1px dashed var(--border)', marginBottom: 12 }}>
      <div style={{ color, fontSize: 10, marginBottom: 4 }}>[{w.time}] → {status.toUpperCase()}</div>
      <div style={{ color: 'var(--text2)', marginTop: 4, whiteSpace: 'pre', fontSize: 11 }}>
        {JSON.stringify(w.payload, null, 2)}
      </div>
    </div>
  );
}
